import { useMemo, useState } from "react";
import ReactSelect from "react-select";
import { getRoleDisplayName } from "@/lib/auth-utils";
import { createReactSelectStyles } from "@/lib/react-select-styles";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { useUsers, useUser } from "@/features/users/hooks/useUsersQueries";
import type { ListUsersQuery, User } from "@/types/user";

interface UserOption {
  value: string;
  label: string;
  email: string;
  role: string;
}

interface UserComboboxProps {
  value?: string;
  onChange: (userId: string, role?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** When set (e.g. EDITOR for Sub-Admin permissions), forwarded to GET /users. */
  listQuery?: Pick<ListUsersQuery, "role" | "limit">;
}

function userLabel(user: Pick<User, "firstName" | "lastName" | "email">): string {
  return `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email;
}

function toUserOption(user: User): UserOption {
  return {
    value: user.id,
    label: userLabel(user),
    email: user.email,
    role: user.role,
  };
}

/**
 * Searchable user picker backed by the users list API.
 * Resolves the selected label via `useUser` when the selection is outside the current page.
 */
export function UserCombobox({
  value,
  onChange,
  disabled,
  placeholder = "اختر مستخدمًا",
  className,
  listQuery,
}: UserComboboxProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, isFetching, isError } = useUsers({
    page: 1,
    limit: listQuery?.limit ?? 50,
    ...(listQuery?.role ? { role: listQuery.role } : {}),
    ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
  });
  const { data: selectedUser } = useUser(value ?? "");

  const options = useMemo(
    () => (data?.items ?? []).map(toUserOption),
    [data?.items],
  );

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const fromList = options.find((option) => option.value === value);
    if (fromList) return fromList;
    if (selectedUser) return toUserOption(selectedUser);
    return { value, label: value, email: "", role: "" };
  }, [value, options, selectedUser]);

  const selectStyles = useMemo(() => createReactSelectStyles<UserOption>(), []);

  return (
    <ReactSelect<UserOption>
      isRtl
      isSearchable
      isClearable
      isDisabled={disabled}
      isLoading={isLoading || isFetching}
      options={options}
      value={selectedOption}
      inputValue={search}
      onInputChange={(nextValue, meta) => {
        if (meta.action === "input-change") setSearch(nextValue);
        if (meta.action === "menu-close") setSearch("");
      }}
      onChange={(option) => {
        onChange(option?.value ?? "", option?.role);
        setSearch("");
      }}
      onMenuClose={() => setSearch("")}
      filterOption={() => true}
      placeholder={placeholder}
      noOptionsMessage={() =>
        isError ? "تعذّر تحميل المستخدمين" : "لا يوجد مستخدمون مطابقون"
      }
      loadingMessage={() => "جاري التحميل..."}
      className={cn("text-sm", className)}
      classNamePrefix="react-select"
      styles={selectStyles}
      formatOptionLabel={(option, { context }) => {
        if (context === "value") return option.label;
        return (
          <div className="flex flex-col gap-0.5 text-right">
            <span className="font-medium">{option.label}</span>
            <span className="text-xs opacity-80">
              {option.email}
              {option.role ? ` · ${getRoleDisplayName(option.role)}` : ""}
            </span>
          </div>
        );
      }}
    />
  );
}
